import './App.css';
import * as api from './api';
import { CommandBar, ContextualMenu, DefaultButton, Dialog, DialogFooter, Icon, initializeIcons, Link, Modal, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, ThemeProvider } from '@fluentui/react';
import { Component, ErrorInfo, } from 'react';
import { store } from './local-storage';
import { appTheme, cn } from './theme';
import { SortMode, TopPivot, twoDays, threeWeeks } from './types';
import { About, Home, ProjectView } from './views';
import { ModalCommander } from './components/ModalCommander';
import { LinkSrvSurvey } from './components/LinkSrvSurvey';
import { ViewAll } from './views/ViewAll/ViewAll';
import { VisualIdentify } from './components/VisualIdentify';
import { SystemView2 } from './views/SystemView2/SystemView2';
import { BigSiteTablePage } from './components/BigSiteTable/BigSiteTable';
import { processLoginCodes, redirectToFrontierAuth } from './api/auth';
import { ShareFeedback } from './components/ShareFeedback';
import { GalMap } from './views/GalMap';
import { NexusView } from './views/NexusView';
import { GGG } from './views/GGG';
import { CmdrSettings } from './api/cmdr';
import { ChangeLog, lastEntry } from './views/ChangeLogView';
import { isMobile } from './util';
import { UploadParser } from './components/UploadParser';

// Initialize icons in case this example uses them
initializeIcons();

interface AppProps {
}

interface AppState {
  pivot: TopPivot;
  pivotArg?: string;

  cmdrSettings?: CmdrSettings;
  cmdr?: string;
  cmdrEdit: boolean;
  showDonate?: boolean;
  showThemes?: boolean;
  showFeedback?: [string, string?];
  showReLogin?: boolean;
  showChangeLog: boolean;
  showJournalParser?: boolean;
}

export class App extends Component<AppProps, AppState> {
  private static scrollBarWidth = 0;
  private static fakeScroll: HTMLDivElement;
  private static instance: App | undefined;
  public static preNav?: (url: string) => void;

  public static get cmdrSettings(): CmdrSettings | undefined { return App.instance?.state.cmdrSettings; }
  public static set cmdrSettings(newValue: CmdrSettings | undefined) { App.instance?.setState({ cmdrSettings: newValue }); }

  public static showFeedback(topic?: string, body?: string) {
    App.instance?.setState({ showFeedback: [topic ?? 'Raven Colonial', body] });
  }

  public static showReLogin() {
    App.instance?.setState({ showReLogin: true });
  }

  public static suspendPageScroll() {
    if (document.body.clientHeight < document.body.scrollHeight) {
      App.fakeScroll.style.display = 'block';
      document.body.style.marginRight = `${App.scrollBarWidth}px`;
      document.body.style.overflow = 'hidden';
    }
  }

  public static resumePageScroll() {
    App.fakeScroll.style.display = 'none';
    document.body.style.marginRight = '0';
    document.body.style.overflow = 'auto';
  }

  constructor(props: AppProps) {
    super(props);
    App.instance = this;

    const [pivot, pivotArg] = this.getPivotFromHash();

    this.state = {
      pivot: pivot,
      pivotArg: pivotArg,
      cmdr: store.cmdrName,
      cmdrEdit: false,
      showChangeLog: window.location.hash === '#changeLog',
    };

    window.onhashchange = () => {
      // console.debug('onhashchange:', ev);
      let [newPivot, pivotArg] = this.getPivotFromHash();
      this.setState({
        pivot: newPivot,
        pivotArg: pivotArg,
        cmdrEdit: false,
        showChangeLog: window.location.hash === '#changeLog',
      });
    };
  }

  componentDidMount(): void {
    this.setStateFromHash();
    this.fetchCmdrSettings();

    // migrate local-storage items?
    if ((store.commoditySort as any) === 'Group by type') { store.commoditySort = SortMode.group; }
    store.migrateLinkedFCs().catch(err => console.error(err));
    localStorage.removeItem('code_verifier');

    // style scrollbars
    App.scrollBarWidth = this.calcScrollBarWidth();
    const docBody = document.getElementsByTagName('html')[0];
    docBody.className = cn.bodyScroll;

    // inject an element to proxy for the scrollbar when it is hidden
    App.fakeScroll = document.createElement('div');
    App.fakeScroll.style.backgroundColor = appTheme.palette.neutralQuaternaryAlt;
    App.fakeScroll.style.zIndex = '10';
    App.fakeScroll.style.position = 'fixed';
    App.fakeScroll.style.top = '0';
    App.fakeScroll.style.right = '0';
    App.fakeScroll.style.bottom = '0';
    App.fakeScroll.style.width = `${App.scrollBarWidth}px`;
    App.fakeScroll.style.display = 'none';
    docBody.appendChild(App.fakeScroll);
  }

  componentWillUnmount(): void {
    delete App.instance;
  }

  calcScrollBarWidth() {
    // Create a temporary div
    const scrollDiv = document.createElement('div');
    scrollDiv.style.opacity = '0'; // ensure not visible
    scrollDiv.style.width = '100px';
    scrollDiv.style.height = '100px';
    scrollDiv.style.overflow = 'scroll';
    scrollDiv.style.position = 'absolute';

    document.body.appendChild(scrollDiv);
    // The scrollbar width is the difference between offsetWidth and clientWidth
    const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    document.body.removeChild(scrollDiv);
    return scrollbarWidth;
  }

  componentDidCatch(error: Error, _errorInfo: ErrorInfo): void {
    let element = document.getElementById('oops');
    if (element) { element.style.display = 'block'; }
    element = document.getElementById('bad-error');
    if (element) { element.innerText = `${window.location}\n\n${error.message ?? "Unknown"}\n\n${error.stack ?? "Unknown"}`; }
  }

  fetchCmdrSettings() {
    if (store.apiKey) {
      api.cmdr.getMyCmdr()
        .then(async data => {
          this.setState({ cmdrSettings: data });

          // migrate if we had no ship sizes or other settings?
          if (!data.shipSizes && !!store.cmdr) {
            const newData = await api.cmdr.updateSettings({
              shipSizes: [store.cmdr.largeMax, store.cmdr.medMax],
              noBuffNerf: store._applyBuffNerf ? undefined : true,
              hideShipTrips: store._hideShipTrips,
            });
            this.setState({ cmdrSettings: newData });
          }
        })
        .catch(err => console.error(err.stack)); // TODO: warning message bar? Maybe a global one?
    } else {
      this.setState({ cmdrSettings: { fid: '', cmdr: '' } });
    }
  }

  setStateFromHash() {
    // console.log(`** App.setStateFromHash: '${window.location.hash}'`);
    const nextState = { ...this.state };

    const params = new URLSearchParams(window.location.hash?.substring(1));
    if (params.has('find')) {
      // searching for build projects
      nextState.pivot = TopPivot.find;
    } else if (params.has('sys')) {
      // viewing a whole system
      nextState.pivot = TopPivot.sys;
    } else if (params.has('build') || params.has('edit')) {
      // viewing a build project
      nextState.pivot = window.location.hash === '#build' ? TopPivot.buildAll : TopPivot.build;
    } else if (params.has('about')) {
      // viewing help content
      nextState.pivot = TopPivot.about;
    } else if (params.has('vis') || window.location.pathname === '/vis') {
      nextState.pivot = TopPivot.vis;
    } else if (params.has('table') || window.location.pathname === '/table') {
      nextState.pivot = TopPivot.table;
    } else if (params.has('cmdr')) {
      // Cmdr specific data
      nextState.pivot = TopPivot.home;
    } else if (nextState.pivot === TopPivot.login) {
      // do not change any state
    } else if (window.location.pathname.endsWith('/user')) {
      nextState.cmdrEdit = true;
    } else if (params.has('map')) {
      nextState.pivot = TopPivot.map;
    } else if (params.has('chain') || params.has('nexus')) {
      nextState.pivot = TopPivot.nexus;
    } else if (params.has('ggg')) {
      nextState.pivot = TopPivot.ggg;
    } else {
      nextState.pivot = TopPivot.home;
    }

    this.setState(nextState);
  }

  getPivotFromHash(): [TopPivot, string | undefined] {
    const params = new URLSearchParams(window.location.hash?.substring(1));
    const pivotArg = params.values().next().value;

    // are we logging in?
    if (window.location.pathname.endsWith('/login')) {
      processLoginCodes();
      return [TopPivot.login, pivotArg];
    } else if (window.location.pathname.endsWith('/user')) {
      return [TopPivot.about, pivotArg];
    }

    // auto switch urls to hash based anchors
    if (window.location.pathname.length > 1) {
      window.location.assign('/#' + window.location.pathname.slice(1));
    }

    if (params.has('find')) {
      return [TopPivot.find, pivotArg];
    } else if (params.has('sys')) {
      return [TopPivot.sys, pivotArg];
    } else if (params.has('build')) {
      return window.location.hash === '#build' ? [TopPivot.buildAll, pivotArg] : [TopPivot.build, pivotArg];
    } else if (params.has('about')) {
      return [TopPivot.about, pivotArg];
    } else if (params.has('vis') || window.location.pathname === '/vis') {
      return [TopPivot.vis, pivotArg];
    } else if (params.has('table') || window.location.pathname === '/table') {
      return [TopPivot.table, pivotArg];
    } else if (params.has('map')) {
      return [TopPivot.map, pivotArg];
    } else if (params.has('chain') || params.has('nexus')) {
      return [TopPivot.nexus, pivotArg];
    } else if (params.has('ggg')) {
      return [TopPivot.ggg, pivotArg];
    } else {
      return [TopPivot.home, pivotArg];
    }
  }

  render() {
    const { cmdrEdit, pivot, showDonate, showThemes, showFeedback, showReLogin, cmdrSettings, showChangeLog, showJournalParser } = this.state;

    const timeSinceLastLogin = Date.now() - new Date(cmdrSettings?.lastLogin ?? '').getTime();
    const tooLong = timeSinceLastLogin > threeWeeks;

    const changeLogColor = (Date.now() - lastEntry.getTime()) < twoDays ? appTheme.palette.themeDarker : undefined;

    return (
      <ThemeProvider theme={appTheme} className='app'>
        <CommandBar
          className={`top-bar ${cn.topBar}`}
          items={[
            {
              className: cn.bBox,
              key: 'home', text: 'Home',
              iconProps: { iconName: 'Home' },
              title: 'Home',
              checked: pivot === 'home',
              href: '/#home',
              onClick: (ev) => {
                const blockNavigate = App.preNav && App.preNav('/#home');
                if (blockNavigate) { ev?.preventDefault(); }
              }
            },
            {
              className: cn.bBox,
              key: 'find', text: 'System',
              iconProps: { iconName: 'HomeGroup' },
              checked: pivot === 'sys',
              href: '/#sys',
              onClick: (ev, i) => {
                const blockNavigate = App.preNav && App.preNav('/#sys');
                if (blockNavigate || window.location.hash.startsWith('#find')) {
                  ev?.preventDefault();
                }
              }
            },
            {
              className: cn.bBox,
              key: 'build', text: 'Build',
              iconProps: { iconName: 'Manufacturing' },
              checked: pivot === 'build',
              href: '/#build',
              onClick: (ev) => {
                const blockNavigate = App.preNav && App.preNav('/#build');
                if (blockNavigate) { ev?.preventDefault(); }
              }
            },
            {
              className: cn.bBox,
              key: 'about', text: 'About',
              iconProps: { iconName: 'Help' },
              checked: pivot === 'about',
              href: '/#about',
              onClick: (ev) => {
                const blockNavigate = App.preNav && App.preNav('/#about');
                if (blockNavigate) { ev?.preventDefault(); }
              }
            },
            {
              key: 'sys-loading',
              title: 'Networking ...',
              iconProps: { iconName: 'Nav2DMapView' },
              onRender: () => {
                return !!cmdrSettings ? null : <div>
                  <Spinner
                    size={SpinnerSize.medium}
                    labelPosition='right'
                    label='Loading ...'
                    style={{ marginTop: 12, cursor: 'default' }}
                  />
                </div>
              },
            },
          ]}
          farItems={[
            {
              className: cn.bBox,
              id: 'view-table', key: 'tab',
              iconProps: { iconName: 'ViewListGroup' },
              iconOnly: true,
              title: 'The table of everything',
              onClick: ev => {
                if (ev?.shiftKey) {
                  this.setState({ showJournalParser: true });
                } else {
                  window.open('/table', 'table');
                }
              },
            },
            {
              className: cn.bBox,
              id: 'view-vis', key: 'vis',
              iconProps: { iconName: 'View' },
              iconOnly: true,
              title: 'Site visual identification guide',
              onClick: () => window.open('/#vis', 'vis'),
            },
            {
              className: cn.bBox,
              id: 'set-theme', key: 'theme',
              iconProps: { iconName: 'Contrast' },
              iconOnly: true,
              title: 'Choose a theme',
              onClick: () => this.setState({ showThemes: !this.state.showThemes }),
            },
            {
              className: cn.bBox,
              id: 'current-cmdr', key: 'current-cmdr',
              title: tooLong ? 'It has been a while since you last logged in' : undefined,
              iconProps: {
                iconName: !store.cmdrName ? 'UserWarning' : tooLong ? 'SkypeCircleClock' : 'Contact',
                style: { color: tooLong ? appTheme.palette.yellowDark : undefined }
              },
              text: this.state.cmdr,
              style: { color: tooLong ? appTheme.palette.yellowDark : undefined },
              onClick: () => this.setState({ cmdrEdit: !this.state.cmdrEdit }),
            }
          ]}
        />
        {showThemes && <ContextualMenu
          target={`#set-theme`}
          onDismiss={() => this.setState({ showThemes: false })}
          items={[
            { key: 'choose', text: 'Choose a theme:', disabled: true },
            { key: 'wite/blue', text: 'Blue (light)', },
            { key: 'dark/blue', text: 'Blue (dark)', },
            { key: 'dark/orange', text: 'Orange (dark)', },
            { key: 'white/green', text: 'Green (light)', },
            { key: 'dark/green', text: 'Green (dark)', },
          ]}
          onItemClick={(e, i) => {
            const newTheme = i?.key.toString() ?? '';
            if (newTheme !== store.theme) {
              store.theme = newTheme;
              window.location.reload();
            }
          }}
          styles={{ container: { margin: -10, padding: 10, border: '1px solid ' + appTheme.palette.themePrimary, } }}
        />}

        {cmdrSettings && this.renderBody()}

        {cmdrEdit && <Modal
          isOpen
          onDismiss={() => this.setState({ cmdrEdit: false })}
        >
          <ModalCommander onComplete={() => this.setState({ cmdrEdit: false })} />
        </Modal>}
        <br />
        <footer className={cn.footer}>
          <div>
            <span>©2026 Raven Colonial Corporation</span>
            <span style={{ color: 'grey' }}> | </span>
            <Link id='send-support' onClick={() => this.setState({ showDonate: true })}>Support<Icon className='icon-inline' iconName='Savings' style={{ textDecoration: 'none', marginLeft: 4 }} /></Link>
            <span style={{ color: 'grey' }}> | </span>
            <LinkSrvSurvey text='About SrvSurvey' />
            <span style={{ color: 'grey' }}> | </span>
            <Link id='send-feedback' onClick={() => App.showFeedback()}>Feedback<Icon className='icon-inline' iconName='Feedback' style={{ textDecoration: 'none', marginLeft: 4 }} /></Link>
            <span style={{ color: 'grey' }}> | </span>
            <LinkSrvSurvey href='https://youtu.be/Kt4MpUJ-ISI?si=FPTNMEBlNP4a3lQl' text="Tutorial" title="Watch CMDR Mechan's tutorial"></LinkSrvSurvey>
            <span style={{ color: 'grey' }}> | </span>
            <Link style={{ color: changeLogColor }} onClick={() => this.setState({ showChangeLog: !showChangeLog })}>Change log{changeLogColor && <Icon iconName='AlarmClock' style={{ marginLeft: 4, textDecoration: 'none' }} />}</Link>
          </div>
        </footer>

        {showDonate && <Dialog
          hidden={false}
          dialogContentProps={{ title: 'Support Raven Colonial' }}
          onDismiss={() => this.setState({ showDonate: false })}
          styles={{ main: { border: '1px solid ' + appTheme.palette.themePrimary, } }}
        >
          Do you like this site? Fantastic!
          <br />
          <br />
          If you would like to help with server costs or buying fresh coffee, donations may be made through <Link href='https://paypal.me/SrvSurvey' target='_blank'>PayPal</Link> or <Link href='https://www.patreon.com/SrvSurvey' target='_blank'>Patreon</Link>.
          <br />
          <br />
          Thank you 😎
          <br />
          <br />
          ~ CMDR Grinning2001
          <DialogFooter>
            <PrimaryButton text="Close" onClick={() => this.setState({ showDonate: false })} />
          </DialogFooter>
        </Dialog>}

        {!!showFeedback && <ShareFeedback topic={showFeedback[0]} body={showFeedback[1]} onDismiss={() => this.setState({ showFeedback: undefined })} />}

        {!!showReLogin && <Dialog
          hidden={false}
          dialogContentProps={{ title: 'Unauthorised?' }}
          styles={{ main: { border: '1px solid ' + appTheme.palette.themePrimary, } }}
          minWidth={420}
        >
          <Icon iconName='Info' style={{ fontSize: 40, float: 'left', marginRight: 10 }} />
          <div>
            Frontier requires a re-login every 30 days.
            <br />
            Would you like to login again now?
          </div>
          <DialogFooter>
            <PrimaryButton text='Yes' iconProps={{ iconName: 'CheckMark' }} onClick={() => redirectToFrontierAuth()} />
            <DefaultButton text='No' iconProps={{ iconName: 'Cancel' }} onClick={() => this.setState({ showReLogin: false })} />
          </DialogFooter>
        </Dialog>}

        {showChangeLog && <>
          <Panel
            isOpen
            isLightDismiss
            headerText='Change Log'
            allowTouchBodyScroll={isMobile()}
            type={PanelType.custom}
            customWidth='800px'
            styles={{
              header: { textTransform: 'capitalize', cursor: 'default' },
              overlay: { backgroundColor: appTheme.palette.blackTranslucent40, cursor: 'default' },
            }}
            onDismiss={() => this.setState({ showChangeLog: false })}
          >
            <ChangeLog />
          </Panel>
        </>}

        {showJournalParser && <>
          <Panel
            isOpen
            isLightDismiss
            headerText='Journal Parser'
            allowTouchBodyScroll={isMobile()}
            type={PanelType.custom}
            customWidth='800px'
            styles={{
              header: { textTransform: 'capitalize', cursor: 'default' },
              overlay: { backgroundColor: appTheme.palette.blackTranslucent40, cursor: 'default' },
            }}
            onDismiss={() => this.setState({ showJournalParser: false })}
          >
            <UploadParser />
          </Panel>
        </>}
      </ThemeProvider>
    );
  }

  renderBody() {
    const { pivot, pivotArg } = this.state;

    switch (pivot) {
      case TopPivot.home: return <Home />;
      case TopPivot.find:
      case TopPivot.sys: {
        const idx = pivotArg?.indexOf('/') ?? -1;
        if (idx >= 0) {
          const systemName = pivotArg?.substring(0, idx) ?? '';
          const savedName = pivotArg?.substring(idx + 1) ?? '';
          return <SystemView2 systemName={systemName} savedName={savedName} />;
        } else {
          return <SystemView2 systemName={pivotArg!} />;
        }
      }
      case TopPivot.build: return <ProjectView buildId={pivotArg} />;
      case TopPivot.buildAll: return <ViewAll />;
      case TopPivot.about: return <About />;
      case TopPivot.vis: return <VisualIdentify buildType={pivotArg} />;
      case TopPivot.table: return <BigSiteTablePage />;
      case TopPivot.login: return <div><Spinner style={{ marginTop: 100 }} size={SpinnerSize.large} label='Logging in ...' /></div>;
      case TopPivot.map: return <GalMap source={pivotArg} />;
      case TopPivot.nexus: return <NexusView id={pivotArg} />;
      case TopPivot.ggg: return <GGG />;
    }
  }

}
